// src/nav/useNavRegistry.ts
import { useTranslation } from 'react-i18next'

import { useFilteredNav } from '@/hooks/useFilteredNav'
import { usePermissionFilteredNav } from '@/hooks/usePermissionFilteredNav'

import type { NavItem } from '@/types/nav-item'
import type { UserRole } from '@/types/user-role'

import { getMemberNav } from './memberNav'
import { getStaffNav } from './staffNav'

/**
 * Navigation Registry
 *
 * Returns the appropriate navigation menu based on user role:
 * - Staff roles (super_admin, gym_manager, assistant, coach): Use unified staffNav with permission filtering
 * - Member role: Use memberNav with membership filtering
 */
export const useNavRegistry = (role: UserRole): NavItem[] => {
  const { t } = useTranslation(['dashboard'])
  const isStaff = ['super_admin', 'gym_manager', 'assistant', 'coach'].includes(role)

  const baseNav = isStaff ? getStaffNav(t) : getMemberNav(t)

  // Apply permission-based filtering for staff
  const permissionFilteredNav = usePermissionFilteredNav(baseNav)

  // Apply membership filtering for members
  const filteredNav = useFilteredNav(permissionFilteredNav)

  return filteredNav
}
